/**
 * The Chakra v3 system for the EHE Operator's Console. Built on `defaultConfig`
 * (for the base conditions: _dark/_light/_hover/_focusVisible/etc. and the default
 * spacing + z-index scales) merged with our product config.
 *
 * MUI has been fully removed — Chakra `@/ui` is the sole UI engine:
 *   - `preflight: true` — Chakra owns the document reset (box-sizing, margins, etc.);
 *     the app canvas (body bg/color/font) is set in `global-css`.
 *   - `cssVarsPrefix: 'ehe'` — all Chakra CSS vars are `--ehe-*`.
 */
import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { tokens, breakpoints } from './tokens';
import { semanticColors, semanticShadows } from './semantic-tokens';
import { globalCss } from './global-css';

const config = defineConfig({
  preflight: true,
  cssVarsPrefix: 'ehe',
  globalCss,
  theme: {
    breakpoints,
    keyframes: {
      'ehe-skeleton-pulse': {
        '0%, 100%': { opacity: 0.55 },
        '50%': { opacity: 1 },
      },
      'ehe-fade-in': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      'ehe-dialog-in': {
        from: { opacity: 0, transform: 'translateY(8px) scale(0.98)' },
        to: { opacity: 1, transform: 'translateY(0) scale(1)' },
      },
      'ehe-sheet-in-right': {
        from: { transform: 'translateX(100%)' },
        to: { transform: 'translateX(0)' },
      },
      'ehe-sheet-in-left': {
        from: { transform: 'translateX(-100%)' },
        to: { transform: 'translateX(0)' },
      },
    },
    tokens: {
      colors: tokens.colors,
      fonts: tokens.fonts,
      fontSizes: tokens.fontSizes,
      fontWeights: tokens.fontWeights,
      lineHeights: tokens.lineHeights,
      letterSpacings: tokens.letterSpacings,
      radii: tokens.radii,
      sizes: tokens.sizes,
      durations: tokens.durations,
      easings: tokens.easings,
    },
    semanticTokens: {
      colors: semanticColors,
      shadows: semanticShadows,
    },
  },
});

export const system = createSystem(defaultConfig, config);
